import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  CircularProgress,
  Typography,
  Divider,
  useTheme,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckIcon from '@mui/icons-material/Check';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TranslationService, {
  setNativeValue,
  getActiveFormLanguage,
  setActiveFormLanguage,
  isEligibleField,
} from '../utils/translationService';

const TARGET_LANGS = [
  { code: 'te', label: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳' },
  { code: 'hi', label: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ta', label: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳' },
];

/**
 * Compact FormTranslateBar with Automatic Live-Translation:
 * When admin selects a language (Telugu, Hindi, Tamil), any entered text
 * automatically translates into that language on-the-fly and on blur.
 */
const FormTranslateBar = ({ fields = null }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const [anchorEl, setAnchorEl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeLang, setActiveLang] = useState(() => getActiveFormLanguage());

  const containerRef = useRef(null);
  const isTranslatingRef = useRef(false);

  // Synchronize across open dialogs/forms AND navbar language selector
  useEffect(() => {
    const handleFormSync = (e) => {
      const lang = e.detail;
      if (lang && lang !== activeLang) {
        setActiveLang(lang);
      }
    };
    const handleAdminSync = (e) => {
      const lang = e.detail?.lang;
      if (lang && lang !== activeLang) {
        setActiveLang(lang);
      }
    };

    window.addEventListener('gpn_form_lang_changed', handleFormSync);
    window.addEventListener('gpn_admin_language_changed', handleAdminSync);
    return () => {
      window.removeEventListener('gpn_form_lang_changed', handleFormSync);
      window.removeEventListener('gpn_admin_language_changed', handleAdminSync);
    };
  }, [activeLang]);

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  /**
   * Translates all eligible fields in the dialog/form to the target language
   */
  const translateAllFields = useCallback(
    async (langCode) => {
      if (isTranslatingRef.current) return;
      isTranslatingRef.current = true;
      setLoading(true);

      try {
        if (fields && Array.isArray(fields) && fields.length > 0) {
          for (const f of fields) {
            const currentVal = f.get() || '';
            if (!currentVal.trim()) continue;

            const translated = await TranslationService.translate(currentVal, langCode);
            f.set(translated);
          }
        } else {
          const dialogOrForm =
            containerRef.current?.closest('.MuiDialog-paper, [role="dialog"], form, .MuiPaper-root') || document;
          const inputs = Array.from(
            dialogOrForm.querySelectorAll('input, textarea, .ql-editor, [contenteditable="true"]')
          ).filter(isEligibleField);

          for (const el of inputs) {
            const isRich = el.isContentEditable || el.classList?.contains('ql-editor');
            const currentVal = isRich ? el.innerHTML : el.value;
            const plain = isRich ? (el.innerText || '').trim() : (el.value || '').trim();
            if (!plain) continue;

            // Remember English source text if current content has Latin letters
            if (/[a-zA-Z]/.test(plain)) {
              el.dataset.gpnOriginal = isRich ? currentVal : plain;
            }

            if (langCode === 'en') {
              const original = el.dataset.gpnOriginal;
              if (original) {
                if (isRich) {
                  el.innerHTML = original;
                  el.dispatchEvent(new Event('input', { bubbles: true }));
                } else {
                  setNativeValue(el, original);
                }
              } else {
                if (isRich) {
                  const translatedHtml = await TranslationService.translateHtml(currentVal, 'en');
                  el.innerHTML = translatedHtml;
                  el.dispatchEvent(new Event('input', { bubbles: true }));
                } else {
                  const translated = await TranslationService.translate(currentVal, 'en');
                  setNativeValue(el, translated);
                }
              }
            } else {
              const source = el.dataset.gpnOriginal || currentVal;
              if (isRich) {
                const translatedHtml = await TranslationService.translateHtml(source, langCode);
                el.innerHTML = translatedHtml;
                el.dispatchEvent(new Event('input', { bubbles: true }));
              } else {
                const translated = await TranslationService.translate(source, langCode);
                setNativeValue(el, translated);
              }
            }
          }
        }
      } catch (err) {
        console.error('[FormTranslateBar] Error translating form:', err);
      } finally {
        isTranslatingRef.current = false;
        setLoading(false);
      }
    },
    [fields]
  );

  const handleSelectLanguage = async (langCode) => {
    handleCloseMenu();
    setActiveLang(langCode);
    setActiveFormLanguage(langCode);

    // Translate all current fields immediately
    await translateAllFields(langCode);
  };

  const activeLangObj = TARGET_LANGS.find((l) => l.code === activeLang);

  return (
    <Box
      ref={containerRef}
      data-gpn-translator="true"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        mr: 'auto',
      }}
    >
      <Tooltip
        title={
          activeLang !== 'en'
            ? `Auto-Translate is ACTIVE in ${activeLangObj?.label} (${activeLangObj?.nativeName}). Click to change.`
            : 'Click to select language for auto-translation'
        }
        arrow
      >
        <Box
          onClick={handleOpenMenu}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.8,
            cursor: 'pointer',
            px: 1.4,
            py: 0.6,
            borderRadius: '8px',
            bgcolor:
              activeLang !== 'en'
                ? isLight
                  ? 'rgba(13, 148, 136, 0.15)'
                  : 'rgba(76, 161, 175, 0.25)'
                : isLight
                ? 'rgba(13, 148, 136, 0.08)'
                : 'rgba(76, 161, 175, 0.12)',
            border:
              activeLang !== 'en'
                ? isLight
                  ? '1.8px solid #0D9488'
                  : '1.8px solid #4CA1AF'
                : isLight
                ? '1.5px solid rgba(13, 148, 136, 0.35)'
                : '1.5px solid rgba(76, 161, 175, 0.35)',
            color: '#0D9488',
            transition: 'all 0.2s ease',
            userSelect: 'none',
            boxShadow:
              activeLang !== 'en'
                ? isLight
                  ? '0 2px 8px rgba(13, 148, 136, 0.2)'
                  : '0 2px 8px rgba(76, 161, 175, 0.25)'
                : 'none',
            '&:hover': {
              bgcolor: isLight ? 'rgba(13, 148, 136, 0.22)' : 'rgba(76, 161, 175, 0.32)',
              borderColor: '#0D9488',
              transform: 'translateY(-1px)',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={18} sx={{ color: '#0D9488' }} />
          ) : activeLang !== 'en' ? (
            <AutoAwesomeIcon sx={{ fontSize: 18, color: isLight ? '#0D9488' : '#64D2E2' }} />
          ) : (
            <TranslateIcon sx={{ fontSize: 19 }} />
          )}

          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: '0.82rem',
              color: isLight ? '#0F766E' : '#64D2E2',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {activeLang !== 'en' ? (
              <>
                <span>{activeLangObj?.flag}</span>
                <span>{activeLangObj?.nativeName}</span>
                <span style={{ fontSize: '0.72rem', opacity: 0.85 }}>(Auto)</span>
              </>
            ) : (
              'Translate'
            )}
          </Typography>
        </Box>
      </Tooltip>

      {/* Language Selection Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            minWidth: 210,
            bgcolor: isLight ? '#FFFFFF' : '#182232',
            border: isLight ? '1.5px solid #CBD5E1' : '1px solid rgba(76, 161, 175, 0.35)',
            boxShadow: isLight
              ? '0 10px 25px rgba(15, 23, 42, 0.15)'
              : '0 15px 35px rgba(0, 0, 0, 0.55)',
            p: 0.5,
          },
        }}
      >
        <Box sx={{ px: 1.5, py: 0.8 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              color: isLight ? '#64748B' : 'rgba(255, 255, 255, 0.5)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Auto-Translate Form
          </Typography>
        </Box>

        <Divider sx={{ my: 0.5 }} />

        {TARGET_LANGS.map((lang) => (
          <MenuItem
            key={lang.code}
            onClick={() => handleSelectLanguage(lang.code)}
            sx={{
              borderRadius: 1.5,
              mx: 0.5,
              my: 0.3,
              fontWeight: activeLang === lang.code ? 700 : 500,
              bgcolor: activeLang === lang.code ? (isLight ? '#F0FDFA' : 'rgba(13, 148, 136, 0.15)') : 'transparent',
              '&:hover': {
                bgcolor: isLight ? '#F1F5F9' : 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 30, fontSize: '1.1rem' }}>
              {lang.flag}
            </ListItemIcon>
            <ListItemText
              primary={`${lang.label} (${lang.nativeName})`}
              secondary="Auto-translates as you type"
              primaryTypographyProps={{
                fontSize: '0.84rem',
                fontWeight: activeLang === lang.code ? 700 : 500,
                color: activeLang === lang.code ? '#0D9488' : isLight ? '#0F172A' : '#FFFFFF',
              }}
              secondaryTypographyProps={{
                fontSize: '0.7rem',
                color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)',
              }}
            />
            {activeLang === lang.code && (
              <CheckIcon sx={{ fontSize: 16, color: '#0D9488', ml: 1 }} />
            )}
          </MenuItem>
        ))}

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onClick={() => handleSelectLanguage('en')}
          sx={{
            borderRadius: 1.5,
            mx: 0.5,
            my: 0.3,
            '&:hover': {
              bgcolor: isLight ? '#F1F5F9' : 'rgba(255, 255, 255, 0.08)',
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 30, fontSize: '1.1rem' }}>
            🇺🇸
          </ListItemIcon>
          <ListItemText
            primary="English (Off / Reset)"
            secondary="Keep text in original English"
            primaryTypographyProps={{
              fontSize: '0.84rem',
              fontWeight: activeLang === 'en' ? 700 : 500,
              color: isLight ? '#475569' : 'rgba(255, 255, 255, 0.8)',
            }}
            secondaryTypographyProps={{
              fontSize: '0.7rem',
              color: isLight ? '#64748B' : 'rgba(255,255,255,0.5)',
            }}
          />
          {activeLang === 'en' && (
            <CheckIcon sx={{ fontSize: 16, color: '#0D9488', ml: 1 }} />
          )}
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default FormTranslateBar;
